import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../legal/legal.css';
import {
    HomeHeroArt,
    CalendarArt,
    ChatArt,
    SeatMapArt,
    CommunityArt,
    PrivateEventArt,
    DashboardArt,
    StoreBadges,
} from './home/HomeArt';
import { useT } from '../i18n/I18nProvider';
import { EVENT_CATEGORY_LOOK, EventGrid, EventGridSkeleton, WebEvent, extractEvents } from './EventGrid';
import { readCachedEvents, writeCachedEvents } from './eventCache';

// ─────────────────────────────────────────────────────────────────────────────
// Home / landing page (route "/"). Mirrors the Help Center's chrome (shared
// Header + Footer via SiteLayout) and tone. Structure:
//   1. App-first disclaimer (the website is a companion; the app is the full
//      experience) + the App Store / Google Play download badges — directly
//      under the header so it's the first thing every visitor sees.
//   2. Live hero, category rail and event highlights.
//   3. Split hero — intro copy on the left, brand illustration on the right.
//   4. Feature sections (calendar, chat, seat map, community, free private
//      events, dashboard) each pairing a self-drawn illustration with copy.
// All strings are translated (de = source/fallback, en, fr).
// ─────────────────────────────────────────────────────────────────────────────

type Feature = {
    art: React.ReactNode;
    titleKey: string;
    descKey: string;
};

const FEATURES: Feature[] = [
    { art: <CalendarArt />, titleKey: 'home.feat.calendar.title', descKey: 'home.feat.calendar.desc' },
    { art: <ChatArt />, titleKey: 'home.feat.chat.title', descKey: 'home.feat.chat.desc' },
    { art: <SeatMapArt />, titleKey: 'home.feat.seatmap.title', descKey: 'home.feat.seatmap.desc' },
    { art: <CommunityArt />, titleKey: 'home.feat.community.title', descKey: 'home.feat.community.desc' },
    { art: <PrivateEventArt />, titleKey: 'home.feat.private.title', descKey: 'home.feat.private.desc' },
    { art: <DashboardArt />, titleKey: 'home.feat.dashboard.title', descKey: 'home.feat.dashboard.desc' },
];

const API = 'https://maineventbackend.seuytel.com/api/';
const HIGHLIGHTS_CACHE_KEY = 'me.events.highlights.v1';

const HERO_PHOTOS = [
    {
        src: 'https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?auto=format&fit=crop&w=900&q=82',
        labelKey: 'home.live.photo.events',
    },
    {
        src: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=900&q=82',
        labelKey: 'home.live.photo.tickets',
    },
    {
        src: 'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?auto=format&fit=crop&w=900&q=82',
        labelKey: 'home.live.photo.memories',
    },
    {
        src: 'https://images.unsplash.com/photo-1753351056802-24fd2345dc2b?auto=format&fit=crop&w=900&q=82',
        labelKey: 'home.live.photo.chat',
    },
];

const LIVE_FEATURE_KEYS = [
    'home.live.feature.calendar',
    'home.live.feature.chat',
    'home.live.feature.tickets',
    'home.live.feature.memories',
    'home.live.feature.dashboard',
    'home.live.feature.services',
];

function CategoryScroller() {
    const { t } = useT();
    const railRef = useRef<HTMLDivElement>(null);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);

    const updateScrollState = () => {
        const el = railRef.current;
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        setCanLeft(el.scrollLeft > 2);
        setCanRight(el.scrollLeft < max - 2);
    };

    useEffect(() => {
        const el = railRef.current;
        if (!el) return;

        updateScrollState();
        const onScroll = () => updateScrollState();
        const onResize = () => updateScrollState();
        const timer = window.setTimeout(updateScrollState, 120);

        el.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onResize);

        return () => {
            window.clearTimeout(timer);
            el.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onResize);
        };
    }, []);

    const move = (direction: -1 | 1) => {
        const el = railRef.current;
        if (!el) return;
        el.scrollBy({
            left: direction * Math.max(260, el.clientWidth * 0.72),
            behavior: 'smooth',
        });
    };

    return (
        <section className="se-home-categories-wrap se-container" aria-label={t('nav.categories')}>
            <button
                type="button"
                className="se-home-cat-arrow se-left"
                aria-label={t('home.categories.left')}
                disabled={!canLeft}
                onClick={() => move(-1)}
            >
                ‹
            </button>

            <div className="se-home-categories" ref={railRef}>
                {EVENT_CATEGORY_LOOK.map((cat) => (
                    <Link
                        key={cat.id}
                        to={`/events?category=${cat.id}`}
                        className="se-home-cat-pill"
                        style={{ ['--cat-color' as any]: cat.color }}
                    >
                        <span aria-hidden="true">{cat.icon}</span>
                        <strong>{t(cat.key)}</strong>
                    </Link>
                ))}
            </div>

            <button
                type="button"
                className="se-home-cat-arrow se-right"
                aria-label={t('home.categories.right')}
                disabled={!canRight}
                onClick={() => move(1)}
            >
                ›
            </button>
        </section>
    );
}

export default function HomePage() {
    const { t } = useT();
    const [highlights, setHighlights] = useState<WebEvent[]>([]);
    const [highlightsLoading, setHighlightsLoading] = useState(true);
    const [highlightsError, setHighlightsError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const cached = readCachedEvents(HIGHLIGHTS_CACHE_KEY);
        if (cached) {
            setHighlights(cached);
            setHighlightsLoading(false);
        } else {
            setHighlightsLoading(true);
        }
        setHighlightsError(false);
        fetch(`${API}search/events/highlight?size=8`, { headers: { Accept: 'application/json' } })
            .then((res) => (res.ok ? res.json() : null))
            .then((payload) => {
                if (!cancelled && payload) {
                    const nextEvents = extractEvents(payload).slice(0, 8);
                    setHighlights(nextEvents);
                    writeCachedEvents(HIGHLIGHTS_CACHE_KEY, nextEvents);
                }
            })
            .catch(() => {
                if (!cancelled && !cached) {
                    setHighlights([]);
                    setHighlightsError(true);
                }
            })
            .finally(() => {
                if (!cancelled) setHighlightsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <>
            {/* app-first disclaimer + store badges — pinned directly under the header */}
            <section className="se-container se-home-top-notice" aria-label={t('home.disclaimer.label')}>
                <div className="se-hc-disclaimer" role="alert">
                    <span className="se-hc-disclaimer-icon" aria-hidden="true">⚠</span>
                    <p>
                        <strong>{t('home.disclaimer.label')}</strong> {t('home.disclaimer.text')}
                    </p>
                </div>
                <div className="se-home-get">
                    <span className="se-home-get-label">{t('home.getApp')}</span>
                    <StoreBadges className="se-home-badges" />
                </div>
            </section>

            <section className="se-home-live-hero">
                <div className="se-home-live-copy">
                    <span className="se-hc-eyebrow">{t('home.live.eyebrow')}</span>
                    <h1>{t('home.live.title')}</h1>
                    <p>{t('home.live.text')}</p>
                    <div className="se-home-live-features" aria-label="Main Events Features">
                        {LIVE_FEATURE_KEYS.map((key) => (
                            <span key={key}>{t(key)}</span>
                        ))}
                    </div>
                    <Link to="/events" className="se-home-live-btn">{t('home.live.cta')}</Link>
                </div>
                <div className="se-home-photo-stage" aria-hidden="true">
                    {HERO_PHOTOS.map((photo, index) => (
                        <div key={photo.labelKey} className={`se-home-photo-card se-home-photo-${index + 1}`}>
                            <img src={photo.src} alt="" />
                            <span>{t(photo.labelKey)}</span>
                        </div>
                    ))}
                </div>
            </section>

            <CategoryScroller />

            <section className="se-home-highlight se-container">
                <div className="se-home-section-head">
                    <span className="se-hc-eyebrow">{t('home.highlights.eyebrow')}</span>
                    <h2>{t('home.highlights.title')}</h2>
                    <Link to="/events">{t('home.highlights.all')}</Link>
                </div>
                {highlightsLoading ? (
                    <EventGridSkeleton count={8} />
                ) : highlightsError ? (
                    <p className="se-event-empty">{t('home.highlights.error')}</p>
                ) : (
                    <EventGrid events={highlights} />
                )}
            </section>

            {/* ── split hero: copy left, illustration right ─────────────────── */}
            <section className="se-home-hero">
                <div className="se-home-hero-inner">
                    <div className="se-home-hero-text">
                        <span className="se-hc-eyebrow se-home-eyebrow">{t('home.hero.eyebrow')}</span>
                        <h1>{t('home.hero.title')}</h1>
                        <p className="se-home-hero-sub">{t('home.hero.text')}</p>
                        <p className="se-home-tagline">{t('home.hero.tagline')}</p>
                    </div>
                    <div className="se-home-hero-art">
                        <HomeHeroArt />
                    </div>
                </div>
            </section>

            <main className="se-content se-home-content">
                {/* features */}
                <h2 className="se-home-feat-heading">{t('home.features.heading')}</h2>
                <p className="se-home-feat-lead">{t('home.features.lead')}</p>

                <div className="se-home-features">
                    {FEATURES.map((f, i) => (
                        <section
                            key={f.titleKey}
                            className={`se-home-feature${i % 2 === 1 ? ' se-home-feature-rev' : ''}`}
                        >
                            <div className="se-home-feature-art">{f.art}</div>
                            <div className="se-home-feature-body">
                                <h3>{t(f.titleKey)}</h3>
                                <p>{t(f.descKey)}</p>
                            </div>
                        </section>
                    ))}
                </div>

                {/* closing CTA */}
                <div className="se-home-cta-band">
                    <h2>{t('home.cta.heading')}</h2>
                    <p>{t('home.cta.text')}</p>
                    <StoreBadges className="se-home-badges" />
                </div>
            </main>
        </>
    );
}
